<!--
录音管理页面组件
被主导航调用
权限点：recording.view（查看）, recording.delete（删除）
简要说明：用于管理系统的录音文件，包括录音列表、播放、下载、删除等功能
-->
<template>
  <div class="recording-management">
    <!-- 搜索筛选区域 -->
    <div class="search-section">
      <el-card class="search-card">
        <div class="search-form">
          <el-row :gutter="12">
            <el-col :span="5">
              <el-input
                v-model="searchForm.device_id"
                placeholder="设备编号"
                clearable
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </el-col>
            <el-col :span="5">
              <el-input
                v-model="searchForm.operator_name"
                placeholder="联络员"
                clearable
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </el-col>
            <el-col :span="6">
              <el-date-picker
                v-model="searchForm.dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-col>
            <el-col :span="6">
              <div class="button-group">
                <el-button
                  type="primary"
                  @click="handleSearch"
                  v-if="hasPermission('recording.view')"
                >
                  <el-icon><Search /></el-icon>
                  搜索
                </el-button>
                <el-button
                  @click="resetSearch"
                  v-if="hasPermission('recording.view')"
                >
                  <el-icon><Refresh /></el-icon>
                  重置
                </el-button>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>

    <!-- 录音列表 -->
    <div class="recording-list">
      <el-card class="list-card">
        <template #header>
          <div class="card-header">
            <span>录音列表</span>
            <div class="header-actions">
              <el-button
                type="danger"
                @click="batchDelete"
                :disabled="selectedRecordings.length === 0"
                v-if="hasPermission('recording.delete')"
              >
                <el-icon><Delete /></el-icon>
                批量删除
              </el-button>
            </div>
          </div>
        </template>

        <el-table
          :data="recordings"
          style="width: 100%"
          table-layout="fixed"
          v-loading="loading"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="55" />
          <el-table-column prop="device_id" label="设备编号" width="160" />
          <el-table-column
            prop="recording_id"
            label="录音ID"
            width="180"
            show-overflow-tooltip
          />
          <el-table-column prop="start_time" label="开始时间" width="180">
            <template #default="scope">
              {{ formatDateTime(scope.row.start_time) }}
            </template>
          </el-table-column>
          <el-table-column prop="duration_seconds" label="时长" width="80">
            <template #default="scope">
              {{ formatDuration(scope.row.duration_seconds) }}
            </template>
          </el-table-column>

          <el-table-column prop="status" label="状态" width="90">
            <template #default="scope">
              <el-tag :type="getStatusType(scope.row.status)">
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <div class="action-buttons">
                <el-button
                  type="primary"
                  size="small"
                  @click="playRecording(scope.row)"
                >
                  <el-icon><VideoPlay /></el-icon>
                  播放
                </el-button>
                <el-button
                  type="success"
                  size="small"
                  @click="downloadRecording(scope.row)"
                >
                  <el-icon><Download /></el-icon>
                  下载
                </el-button>
                <el-button
                  type="danger"
                  size="small"
                  @click="deleteRecording(scope.row)"
                  v-if="hasPermission('recording.delete')"
                >
                  <el-icon><Delete /></el-icon>
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="table-footer">
          <el-pagination
            v-model:current-page="pagination.page"
            v-model:page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 播放录音对话框 -->
    <el-dialog
      v-model="playDialogVisible"
      title="播放录音"
      width="600px"
      @close="closePlayDialog"
    >
      <div class="audio-player">
        <audio
          ref="audioPlayer"
          controls
          style="width: 100%"
          :src="currentRecording?.audio_url"
        >
          您的浏览器不支持音频播放
        </audio>
      </div>
      <div class="recording-info" v-if="currentRecording">
        <p><strong>设备编号:</strong> {{ currentRecording.device_id }}</p>
        <p><strong>录音ID:</strong> {{ currentRecording.recording_id }}</p>
        <p>
          <strong>开始时间:</strong>
          {{ formatDateTime(currentRecording.start_time) }}
        </p>
        <p>
          <strong>时长:</strong>
          {{ formatDuration(currentRecording.duration_seconds) }}
        </p>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, nextTick } from "vue";
import {
  Search,
  Refresh,
  Delete,
  Download,
  VideoPlay,
} from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { useStore } from "vuex";
import { formatDateTime } from "@/utils/dateTimeUtils";
import { DataService } from "@/services/dataService";
import config from "@/config";

// 中文注释：引入全局store
const store = useStore();

// 权限检查函数
const hasPermission = (perm: string) => store.getters.hasPermission(perm);

// 响应式数据
const loading = ref(false);
const playDialogVisible = ref(false);
const currentRecording = ref<any | null>(null);
const selectedRecordings = ref<any[]>([]);
const audioPlayer = ref<HTMLAudioElement | null>(null);

// 搜索表单
const searchForm = reactive({
  device_id: "",
  operator_name: "",
  dateRange: [] as string[],
});

// 分页数据
const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0,
});

// 录音列表数据
const recordings = ref<any[]>([]);

// 获取录音列表（真实接口）
const fetchRecordings = async () => {
  loading.value = true;
  try {
    const params: any = {
      page: pagination.page,
      size: pagination.pageSize,
      device_id: searchForm.device_id || undefined,
      operator_name: searchForm.operator_name || undefined,
      start_date: searchForm.dateRange?.[0] || undefined,
      end_date: searchForm.dateRange?.[1] || undefined,
    };
    // 直接调用后端REST：/api/v1/recordings
    const url = config.apiUrls
      ? (config.apiUrls as any)["RECORDINGS"]
      : undefined;
    const requestUrl = url || `${config.apiBaseUrl}/recordings`;
    const data = await (
      await import("@/services/apiClient")
    ).default.get(requestUrl, { params });
    // 期望返回 DataResponse<{ total, recordings }>
    const payload = data?.data?.data || data?.data || {};
    const list = Array.isArray(payload.recordings) ? payload.recordings : [];
    // 中文注释：前端隐藏已删除的录音项，不改动后端
    recordings.value = list.filter((r: any) => r?.status !== "deleted");
    pagination.total = payload.total || 0;
  } catch (error) {
    console.error("获取录音列表失败:", error);
    ElMessage.error("Failed to load recordings");
  } finally {
    loading.value = false;
  }
};

// 搜索
const handleSearch = () => {
  pagination.page = 1;
  fetchRecordings();
};

// 重置搜索
const resetSearch = () => {
  searchForm.device_id = "";
  searchForm.operator_name = "";
  searchForm.dateRange = [] as any;
  handleSearch();
};

// 刷新录音列表
const refreshRecordings = () => {
  fetchRecordings();
};

// 播放录音：获取直链并在对话框中播放
const playRecording = async (recording: any) => {
  try {
    if (!recording?.recording_id && !recording?.id) {
      ElMessage.error("Invalid recording id");
      return;
    }
    const rid = recording.recording_id || recording.id;
    const baseUrl = `${config.apiBaseUrl}/recordings`;
    const client = (await import("@/services/apiClient")).default;
    const resp = await client.get(`${baseUrl}/${rid}/download-url`);
    const url = resp?.data?.data?.url || resp?.data?.url;
    if (!url) {
      ElMessage.error("Failed to get playback url");
      return;
    }
    currentRecording.value = { ...(recording || {}), audio_url: url };
    playDialogVisible.value = true;
    await nextTick();
    try {
      const audio = audioPlayer.value;
      if (audio) {
        audio.src = url;
        await audio.play();
      }
    } catch (e) {
      console.warn("Auto play failed; user interaction may be required.", e);
    }
  } catch (error) {
    console.error("播放失败:", error);
    ElMessage.error("Play failed");
  }
};

// 关闭播放对话框
const closePlayDialog = () => {
  playDialogVisible.value = false;
  currentRecording.value = null;
};

// 下载录音（先请求鉴权接口获取直链，优先弹出保存对话框）
const downloadRecording = async (recording: any) => {
  try {
    if (!recording?.recording_id && !recording?.id) {
      ElMessage.error("Invalid recording id");
      return;
    }
    const rid = recording.recording_id || recording.id;
    const baseUrl = `${config.apiBaseUrl}/recordings`;
    const client = (await import("@/services/apiClient")).default;
    const resp = await client.get(`${baseUrl}/${rid}/download-url`);
    const url = resp?.data?.data?.url || resp?.data?.url;
    if (!url) {
      ElMessage.error("Failed to get download url");
      return;
    }

    const suggestedName = `${recording?.recording_id || rid}.mp3`;

    // 优先使用 File System Access API
    // 兼容性回退：使用 <a download> 触发浏览器保存
    const anyWindow: any = window as any;
    if (anyWindow && typeof anyWindow.showSaveFilePicker === "function") {
      try {
        const handle = await anyWindow.showSaveFilePicker({
          suggestedName,
          types: [
            {
              description: "Audio MP3",
              accept: { "audio/mpeg": [".mp3"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        const respBin = await fetch(url, { method: "GET" });
        const blob = await respBin.blob();
        await writable.write(blob);
        await writable.close();
        ElMessage.success("Saved successfully");
        return;
      } catch (e) {
        // 若用户取消或写入失败，回退为普通下载
      }
    }

    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    ElMessage.success("Starting download...");
  } catch (error) {
    console.error("下载失败:", error);
    ElMessage.error("Download failed");
  }
};

// 删除录音
const deleteRecording = async (recording: any) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除录音 ${recording.recording_id || recording.id} 吗？`,
      "确认删除",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    const rid = recording.recording_id || recording.id;
    const baseDeleteUrl =
      (config.apiUrls && (config.apiUrls as any)["RECORDINGS"]) ||
      `${config.apiBaseUrl}/recordings`;
    await (
      await import("@/services/apiClient")
    ).default.delete(`${baseDeleteUrl}/${rid}`);
    ElMessage.success("Deleted successfully");
    fetchRecordings();
  } catch (error: any) {
    if (error !== "cancel") {
      console.error("删除失败:", error);
      ElMessage.error("Delete failed");
    }
  }
};

// 批量删除
const batchDelete = async () => {
  if (selectedRecordings.value.length === 0) {
    ElMessage.warning("请选择要删除的录音");
    return;
  }

  try {
    await ElMessageBox.confirm(
      `确定要删除选中的 ${selectedRecordings.value.length} 个录音吗？`,
      "确认批量删除",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    const ids = selectedRecordings.value.map((r) => r.recording_id || r.id);
    // 后端如需批量删除API可扩展；当前逐个删除
    const client = (await import("@/services/apiClient")).default;
    const baseDeleteUrl =
      (config.apiUrls && (config.apiUrls as any)["RECORDINGS"]) ||
      `${config.apiBaseUrl}/recordings`;
    await Promise.all(
      ids.map((rid: string) => client.delete(`${baseDeleteUrl}/${rid}`))
    );
    ElMessage.success("Batch deleted successfully");
    fetchRecordings();
  } catch (error: any) {
    if (error !== "cancel") {
      console.error("批量删除失败:", error);
      ElMessage.error("Batch delete failed");
    }
  }
};

// 选择变化
const handleSelectionChange = (selection: any[]) => {
  selectedRecordings.value = selection;
};

// 分页处理
const handleSizeChange = (size: number) => {
  pagination.pageSize = size;
  pagination.page = 1;
  fetchRecordings();
};

const handleCurrentChange = (page: number) => {
  pagination.page = page;
  fetchRecordings();
};

// 工具函数
const formatDuration = (seconds?: number) => {
  const total = Number(seconds || 0);
  const minutes = Math.floor(total / 60);
  const remainingSeconds = total % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const formatFileSize = (bytes?: number) => {
  const b = Number(bytes || 0);
  if (b === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"] as const;
  const i = Math.floor(Math.log(b) / Math.log(k));
  return parseFloat((b / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

type TagType = "primary" | "success" | "warning" | "info" | "danger";
const getStatusType = (status: string): TagType => {
  const statusMap: Record<string, TagType> = {
    completed: "success",
    processing: "warning",
    failed: "danger",
    recording: "info",
  };
  return statusMap[status] || "info";
};

const getStatusText = (status: string) => {
  const statusMap: Record<string, string> = {
    completed: "已完成",
    processing: "处理中",
    failed: "失败",
    recording: "录制中",
  };
  return statusMap[status] || status;
};

// 页面加载时获取数据
onMounted(() => {
  fetchRecordings();
});
</script>

<style lang="scss" scoped>
.recording-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h2 {
    margin: 0;
    color: #2c3e50;
  }

  .header-actions {
    display: flex;
    gap: 10px;
  }
}

.search-section {
  margin-bottom: 20px;

  .search-card {
    .search-form {
      .el-row {
        align-items: center;
      }
    }
  }
}

.recording-list {
  .list-card {
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .action-buttons {
      display: flex;
      gap: 4px;
      align-items: center;
      justify-content: center;
    }
  }
}

.table-footer {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.audio-player {
  margin-bottom: 20px;
}

.recording-info {
  p {
    margin: 8px 0;
    color: #666;
  }
}

/* 按钮组样式 */
.button-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* 表格列居中对齐 */
:deep(.el-table th),
:deep(.el-table td) {
  text-align: center;
}

/* 操作列按钮居中 */
:deep(.el-table .action-buttons) {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}
</style>
